import { z } from 'zod';
import { FractionSchema, IdSchema, LocalDateSchema, MoneySchema } from '../common/index.js';

/**
 * Clés des paramètres légaux et forme de leur valeur (référentiel-métier §1.4, PAR-01 à PAR-35).
 * Les valeurs elles-mêmes sont des données datées (`legal_parameters`, seeds L02, moteur L13) :
 * aucune n'est écrite ici, pour qu'un changement de loi ne demande jamais de modifier du code.
 */

const mois = z.number().int().min(0).max(600);
const annees = z.number().int().min(0).max(100);
const jours = z.number().int().min(0).max(3650);
const pourcent = z.number().min(0).max(100);
const heure = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const FORMES_VALEUR = {
  mois,
  annees,
  jours,
  pourcent,
  date: LocalDateSchema,
  fraction: FractionSchema,
  montant: MoneySchema,
  entier: z.number().int().min(0),
  secondes: z.number().int().min(0),
  delai_opposition: z.object({ moisStandard: mois, moisEtendu: mois, seuilIndivisaires: z.number().int().min(1) }),
  anciennete_zones: z.object({ droitCommun: annees, zonesPrioritaires: annees }),
  creneaux: z.object({
    debut: heure,
    fin: heure,
    joursExclus: z.array(z.enum(['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'])),
  }),
  cadence: z
    .array(
      z.object({
        decalageJours: z.number().int().min(0),
        action: z.enum(['message', 'appel', 'decision', 'recurrent_mensuel']),
        canal: z.enum(['whatsapp_brigitte', 'whatsapp_isabelle', 'email_cabinet', 'telephone']).nullable(),
        modele: z
          .string()
          .regex(/^[A-Z]\d$/)
          .nullable(),
      }),
    )
    .min(1),
  frequence: z.enum(['hebdomadaire', 'mensuelle']),
} as const;

export type FormeValeur = keyof typeof FORMES_VALEUR;
export type StatutParametre = 'verifie' | 'a_valider' | 'decide' | 'propose';

export interface DefinitionParametreLegal {
  id: string;
  cle: string;
  libelle: string;
  forme: FormeValeur;
  source: string;
  statut: StatutParametre;
}

const par = (id: number, cle: string, libelle: string, forme: FormeValeur, source: string, statut: StatutParametre) =>
  ({ id: `PAR-${String(id).padStart(2, '0')}`, cle, libelle, forme, source, statut }) as const;

export const PARAMETRES_LEGAUX = [
  par(
    1,
    'declaration.delai_mois.domicile',
    'Délai de déclaration, décès dans le département du domicile',
    'mois',
    'CGI art. 642',
    'verifie',
  ),
  par(2, 'declaration.delai_mois.hors', 'Délai de déclaration, autres cas', 'mois', 'CGI art. 642', 'verifie'),
  par(3, 'declaration.interets_pourcent_mois', 'Intérêts de retard par mois, dès le 7e mois', 'pourcent', 'CGI art. 1727', 'verifie'),
  par(4, 'declaration.majoration_pourcent', 'Majoration de retard dès le 13e mois', 'pourcent', 'CGI art. 1728', 'verifie'),
  par(
    5,
    'declaration.seuil_preparation_annees',
    'Seuil « déclaration à préparer » depuis le décès',
    'annees',
    'Pratique d’Isabelle, LPF à vérifier, BETA-02',
    'a_valider',
  ),
  par(6, 'option.delai_annees', 'Délai d’option successorale', 'annees', 'C. civ. art. 780', 'verifie'),
  par(7, 'sommation.delai_minimal_mois', 'Sommation : délai minimal après le décès', 'mois', 'C. civ. art. 771', 'verifie'),
  par(8, 'sommation.reponse_mois', 'Sommation : délai de réponse', 'mois', 'C. civ. art. 772', 'verifie'),
  par(
    9,
    'renonciation.acte_naissance_mois_max',
    'Ancienneté maximale de l’acte de naissance pour renoncer',
    'mois',
    'Pratique des greffes',
    'verifie',
  ),
  par(10, 'prescription.guadeloupe.annees', 'Prescription acquisitive en Guadeloupe', 'annees', 'Loi 2024-322 art. 51-III', 'verifie'),
  par(
    11,
    'prescription.guadeloupe.fin',
    'Fin du régime de prescription décennale',
    'date',
    'Loi 2024-322 (PPL 2218 en attente)',
    'verifie',
  ),
  par(
    12,
    'prescription.pratique_notaires_annees',
    'Exigence pratique des notaires locaux (bâti seulement)',
    'annees',
    'Pratique rapportée par Isabelle',
    'a_valider',
  ),
  par(
    13,
    'prescription.contestation_annees',
    'Délai de contestation de la notoriété acquisitive',
    'annees',
    'Rapporté par Isabelle',
    'a_valider',
  ),
  par(14, 'prescription.honoraires_minimum', 'Honoraires minimaux d’une prescription', 'montant', 'Isabelle', 'decide'),
  par(
    15,
    'letchimy.anciennete_annees',
    'Letchimy : ancienneté de la succession',
    'annees',
    'Loi 2018-1244 (PPL 450 non adoptée)',
    'verifie',
  ),
  par(
    16,
    'letchimy.majorite',
    'Letchimy : majorité des droits en pleine propriété (strictement plus de)',
    'fraction',
    'Loi 2018-1244',
    'verifie',
  ),
  par(
    17,
    'letchimy.opposition_mois',
    'Letchimy : délai d’opposition (étendu si 10 indivisaires ou un résident à l’étranger)',
    'delai_opposition',
    'Loi 2018-1244, décret 2020-1324',
    'verifie',
  ),
  par(18, 'letchimy.fin', 'Letchimy : date de fin du dispositif', 'date', 'Loi 2018-1244', 'verifie'),
  par(19, 'attestation.publication_mois', 'Attestation immobilière : publication après réquisition', 'mois', 'Décret 55-22', 'verifie'),
  par(
    20,
    'attestation.dispense_partage_mois',
    'Dispense d’attestation si partage publié après le décès',
    'mois',
    'Décret 55-22',
    'verifie',
  ),
  par(
    21,
    'conjoint.option_delai_mois',
    'Conjoint survivant : délai pour opter après demande écrite (silence = usufruit)',
    'mois',
    'C. civ. art. 758-3',
    'a_valider',
  ),
  par(
    22,
    'attestation.signature_deux_tiers',
    'Signature de l’attestation si les autres se taisent',
    'fraction',
    'Pratique rapportée par Isabelle',
    'a_valider',
  ),
  par(
    23,
    'biens_sans_maitre.anciennete_annees',
    'Biens sans maître : ancienneté de la succession',
    'anciennete_zones',
    'CG3P art. L.1123-1',
    'verifie',
  ),
  par(24, 'biens_sans_maitre.taxe_impayee_annees', 'Biens sans maître : taxe foncière impayée', 'annees', 'CG3P art. L.1123-1', 'verifie'),
  par(25, 'devolution.degre_max', 'Degré successible maximal', 'entier', 'C. civ. art. 745', 'verifie'),
  par(26, 'collecte.marge_jours', 'Marge de collecte avant la signature', 'jours', 'Décision du 14/10', 'decide'),
  par(27, 'relance.quote_part_jours', 'Relance d’une quote-part impayée', 'jours', 'Modèle P1', 'propose'),
  par(28, 'relance.cadence_heritier', 'Cadence de relance d’un héritier', 'cadence', 'Présentation §07', 'propose'),
  par(29, 'relance.plafond_hebdo', 'Plafond de messages par semaine et par personne, tous canaux', 'entier', 'Présentation §07', 'decide'),
  par(30, 'relance.creneaux', 'Créneaux d’envoi (fuseau de la personne)', 'creneaux', 'Présentation §07', 'decide'),
  par(
    31,
    'relance.appel_apres_lus_sans_reponse',
    'Déclenchement d’un appel après messages lus sans réponse',
    'entier',
    'Présentation §07, D2',
    'propose',
  ),
  par(
    32,
    'clerc.seuil_transmission_pourcent',
    'Complétude du tableau des vivants pour transmettre à la clerc',
    'pourcent',
    'Isabelle',
    'propose',
  ),
  par(33, 'cloture.acces_externe_jours', 'Accès extérieur après clôture', 'jours', 'Décision provisoire, BETA-05', 'propose'),
  par(34, 'video.accueil_duree_max_s', 'Durée maximale d’une vidéo d’accueil', 'secondes', 'Isabelle', 'decide'),
  par(35, 'relance.greffe_frequence', 'Relance du greffe tant que l’attestation manque', 'frequence', 'Présentation', 'propose'),
] as const satisfies readonly DefinitionParametreLegal[];

export type CleLegale = (typeof PARAMETRES_LEGAUX)[number]['cle'];
export const CLES_LEGALES = PARAMETRES_LEGAUX.map((p) => p.cle) as unknown as readonly [CleLegale, ...CleLegale[]];

export function definitionParametre(cle: CleLegale): (typeof PARAMETRES_LEGAUX)[number] {
  const d = PARAMETRES_LEGAUX.find((p) => p.cle === cle);
  if (!d) throw new Error(`Paramètre légal inconnu : ${cle}`);
  return d;
}

export const schemaValeurLegale = (cle: CleLegale): z.ZodType => FORMES_VALEUR[definitionParametre(cle).forme];

export const valeurLegaleValide = (cle: CleLegale, valeur: unknown): boolean => schemaValeurLegale(cle).safeParse(valeur).success;

const champsParametre = {
  key: z.enum(CLES_LEGALES),
  value: z.unknown(),
  effectiveFrom: LocalDateSchema,
  effectiveTo: LocalDateSchema.nullable(),
  sourceUrl: z.url().nullable(),
  note: z.string().nullable(),
};

type ParametreBrut = { key: CleLegale; value: unknown; effectiveFrom: string; effectiveTo: string | null };

const coherent = (p: ParametreBrut, ctx: z.core.$RefinementCtx<ParametreBrut>): void => {
  if (!valeurLegaleValide(p.key, p.value)) ctx.addIssue({ code: 'custom', message: 'valeur non conforme à la clé', path: ['value'] });
  if (p.effectiveTo !== null && p.effectiveTo < p.effectiveFrom) {
    ctx.addIssue({ code: 'custom', message: 'fin de validité avant le début', path: ['effectiveTo'] });
  }
};

export const LegalParameterSchema = z
  // changedBy : null pour une valeur semée par le référentiel (aucun auteur), jamais un auteur inventé (RFC-L51-001 §3 bis).
  .object({ id: IdSchema, ...champsParametre, changedBy: IdSchema.nullable() })
  .superRefine(coherent)
  .meta({ id: 'LegalParameter', description: 'Valeur datée d’un paramètre légal, avec sa source.' });

export const LegalParameterCreateSchema = z.object(champsParametre).superRefine(coherent).meta({ id: 'LegalParameterCreate' });
